import type {
  Project,
  Allocation,
  Employee,
  Application,
  AppAssignment,
  CloudSpend,
  CostAllocation,
  SPListItem,
} from '@/models/types';

export interface ProjectCost {
  projectId: string;
  projectCode: string;
  projectTitle: string;
  /** Cloud spend directly attributed to this project (via AzureSubscriptionIds) */
  infraCost: number;
  /** License cost weighted by allocation percent across allocated members */
  licenseCost: number;
  /** Total */
  totalCost: number;
  /** Number of members allocated */
  memberCount: number;
  /** Cloud subscriptions linked */
  subscriptionIds: string[];
}

export interface EmployeeCost {
  employeeId: string;
  name: string;
  email: string;
  department: string;
  appCount: number;
  monthlyLicenseCost: number;
  allocations: { projectCode: string; percent: number }[];
}

export interface LicenseSummary {
  appId: string;
  appTitle: string;
  category: string;
  vendor: string;
  assignedCount: number;
  costPerLicense: number;
  totalMonthlyCost: number;
}

/**
 * Calculate monthly cost for a single project.
 *
 * Two sources of infra-cost attribution, with the new one taking precedence
 * when present:
 *
 *   1. NEW (preferred) — `OC_CostAllocations` rows for this project + ResourceType=Subscription.
 *      Each row carries a percent (0–100). Project gets that share of the sub's spend.
 *      Allows one sub to be split across multiple projects.
 *
 *   2. LEGACY (fallback) — `Project.AzureSubscriptionIds` (comma list of sub GUIDs).
 *      Treated as 100% to this project for each linked sub. Used only if there
 *      are no allocation rows for the (project, sub) pair.
 *
 * License cost: sum across allocated employees of (their license cost × normalized
 * member-allocation %), unchanged.
 */
export function calculateProjectCost(
  project: SPListItem<Project & { AzureSubscriptionIds?: string; GCPProjectIds?: string }>,
  allocations: SPListItem<Allocation>[],
  employees: SPListItem<Employee>[],
  apps: SPListItem<Application>[],
  assignments: SPListItem<AppAssignment>[],
  cloudSpend: SPListItem<CloudSpend>[],
  costAllocations: SPListItem<CostAllocation>[] = [],
  /** "As-of" date used for time-based attribution. Default = today.
   *  Pass a historical date (e.g. "2026-03-15") to compute what the
   *  cost looked like on that day — only allocations active on that
   *  date contribute. Lets us preserve true historical cost when
   *  members shift between projects. */
  asOfDate?: string,
): ProjectCost {
  const projectId = project.id;
  const projectIdNum = Number(projectId);

  // Legacy: subs / GCP projects listed directly on the project
  const split = (s: string | undefined) =>
    (s ?? '').split(/[,\s;]+/).map((x) => x.trim()).filter(Boolean);
  const legacyAzure = split(
    (project.fields as { AzureSubscriptionIds?: string }).AzureSubscriptionIds,
  );
  const legacyGcp = split(
    (project.fields as { GCPProjectIds?: string }).GCPProjectIds,
  );

  // Active on a given date — used for both sub allocations and member allocations
  const today = (asOfDate ?? new Date().toISOString()).slice(0, 10);
  const isActive = (a: { StartDate?: string; EndDate?: string }) =>
    (!a.StartDate || a.StartDate.slice(0, 10) <= today) &&
    (!a.EndDate || a.EndDate === '' || a.EndDate.slice(0, 10) >= today);
  const subAllocsForProject = costAllocations.filter(
    (a) =>
      a.fields.ProjectId === projectIdNum &&
      a.fields.ResourceType === 'Subscription' &&
      isActive(a.fields),
  );

  // Map of subId → spend
  const spendBySub = new Map<string, number>();
  for (const s of cloudSpend) {
    spendBySub.set(
      s.fields.SubscriptionId,
      (spendBySub.get(s.fields.SubscriptionId) ?? 0) + (s.fields.Amount || 0),
    );
  }

  // Subs covered by an explicit allocation — those use percent attribution.
  const allocatedSubIds = new Set(subAllocsForProject.map((a) => a.fields.ResourceId));

  let infraCost = 0;
  // 1. percent-weighted spend from allocations
  for (const a of subAllocsForProject) {
    const total = spendBySub.get(a.fields.ResourceId) ?? 0;
    infraCost += total * (a.fields.AllocationPercent / 100);
  }
  // 2. legacy 100% mapping for any sub NOT covered by an allocation
  for (const subId of [...legacyAzure, ...legacyGcp]) {
    if (allocatedSubIds.has(subId)) continue; // already counted via allocation
    infraCost += spendBySub.get(subId) ?? 0;
  }

  const linkedSubIds = [
    ...new Set([...subAllocsForProject.map((a) => a.fields.ResourceId), ...legacyAzure, ...legacyGcp]),
  ];

  // Member allocations active on the as-of date. Crucial for true
  // historical attribution: if Alice was on Project A until March 31
  // and on Project B from April 1, asking for asOfDate="2026-03-15"
  // attributes her licence cost to A, while asOfDate="2026-04-15"
  // attributes it to B. Without this filter, both would show full cost.
  const projectAllocs = allocations.filter(
    (a) =>
      a.fields.ProjectId === Number(projectId) &&
      isActive(a.fields),
  );

  // App cost by id
  const appCostMap = new Map<number, number>();
  for (const app of apps) {
    appCostMap.set(Number(app.id), app.fields.MonthlyCostPerLicense ?? 0);
  }

  // Employee → total monthly license cost
  const empCostMap = new Map<number, number>();
  for (const a of assignments) {
    if (a.fields.Status !== 'Active') continue;
    const cost = appCostMap.get(a.fields.AppId) ?? 0;
    empCostMap.set(
      a.fields.EmployeeId,
      (empCostMap.get(a.fields.EmployeeId) ?? 0) + cost,
    );
  }

  // Employee → total allocation % across ALL projects, but only counting
  // allocations active on the as-of date. This way, when Alice's allocation
  // to Project A ends on March 31 and Project B starts April 1, on April 1
  // her total is just her Project B percent — not a phantom sum that
  // dilutes her cost share between two projects.
  const empTotalAllocMap = new Map<number, number>();
  for (const a of allocations) {
    if (!isActive(a.fields)) continue;
    empTotalAllocMap.set(
      a.fields.EmployeeId,
      (empTotalAllocMap.get(a.fields.EmployeeId) ?? 0) +
        (a.fields.AllocationPercent ?? 0),
    );
  }

  // License cost = sum of normalized shares
  let licenseCost = 0;
  for (const alloc of projectAllocs) {
    const empId = alloc.fields.EmployeeId;
    const empLicenseCost = empCostMap.get(empId) ?? 0;
    const thisAlloc = alloc.fields.AllocationPercent ?? 0;
    const totalAlloc = empTotalAllocMap.get(empId) ?? thisAlloc;
    // Normalize: if total > 100, share proportionally; else share of 100
    const denominator = Math.max(100, totalAlloc);
    const share = empLicenseCost * (thisAlloc / denominator);
    licenseCost += share;
  }

  void employees; // kept for future expansion

  return {
    projectId,
    projectCode: project.fields.Code,
    projectTitle: project.fields.Title,
    infraCost: Math.round(infraCost * 100) / 100,
    licenseCost: Math.round(licenseCost * 100) / 100,
    totalCost: Math.round((infraCost + licenseCost) * 100) / 100,
    memberCount: projectAllocs.length,
    subscriptionIds: linkedSubIds,
  };
}

/**
 * Compute per-employee monthly license cost.
 */
export function calculateEmployeeCosts(
  employees: SPListItem<Employee>[],
  apps: SPListItem<Application>[],
  assignments: SPListItem<AppAssignment>[],
  allocations: SPListItem<Allocation>[],
  projects: SPListItem<Project>[],
): EmployeeCost[] {
  const appCostMap = new Map<number, number>();
  for (const app of apps) {
    appCostMap.set(Number(app.id), app.fields.MonthlyCostPerLicense ?? 0);
  }
  const projectMap = new Map<number, string>();
  for (const p of projects) {
    projectMap.set(Number(p.id), p.fields.Code);
  }

  return employees
    .filter((e) => e.fields.Status === 'Active')
    .map((emp) => {
      const empId = Number(emp.id);
      const empAssignments = assignments.filter(
        (a) => a.fields.EmployeeId === empId && a.fields.Status === 'Active',
      );
      const monthlyLicenseCost = empAssignments.reduce(
        (s, a) => s + (appCostMap.get(a.fields.AppId) ?? 0),
        0,
      );
      const empAllocs = allocations
        .filter((a) => a.fields.EmployeeId === empId)
        .map((a) => ({
          projectCode: projectMap.get(a.fields.ProjectId) ?? 'Unknown',
          percent: a.fields.AllocationPercent ?? 0,
        }));

      return {
        employeeId: emp.id,
        name: emp.fields.Title,
        email: emp.fields.Email,
        department: emp.fields.Department,
        appCount: empAssignments.length,
        monthlyLicenseCost: Math.round(monthlyLicenseCost * 100) / 100,
        allocations: empAllocs,
      };
    });
}

/**
 * Compute license summary per app.
 */
export function calculateLicenseSummary(
  apps: SPListItem<Application>[],
  assignments: SPListItem<AppAssignment>[],
): LicenseSummary[] {
  const activeAssignments = assignments.filter(
    (a) => a.fields.Status === 'Active',
  );

  return apps
    .map((app) => {
      const appId = Number(app.id);
      const count = activeAssignments.filter((a) => a.fields.AppId === appId).length;
      const costPer = app.fields.MonthlyCostPerLicense ?? 0;
      return {
        appId: app.id,
        appTitle: app.fields.Title,
        category: app.fields.Category,
        vendor: app.fields.Vendor,
        assignedCount: count,
        costPerLicense: costPer,
        totalMonthlyCost: Math.round(count * costPer * 100) / 100,
      };
    })
    .sort((a, b) => b.totalMonthlyCost - a.totalMonthlyCost);
}
